<script setup lang="ts">
import { inject, ref } from 'vue';
import type { Ref } from 'vue';
import VirtualScroller from 'primevue/virtualscroller';
import type {
  LaneUpdate,
  Opportunity,
  OpportunityBase,
  OpportunityType,
  Swimlane,
} from '@apparatix/types/Leads';
import { useOpportunities } from '@apparatix/composables';
import Lane from '@apparatix/leads/Lane.vue';
import LeadCard from '@apparatix/leads/LeadCard.vue';
import OpportunityLostDialog from './OpportunityLostDialog.vue';

interface SwimlanesProps {
  visibleSwimlanes: Swimlane[];
  allSwimlanes: Swimlane[];
  opportunities: OpportunityBase[];
  type: OpportunityType;
}

const props = defineProps<SwimlanesProps>();
const showOpportunityLostDialog = ref(false);
const laneUpdate = ref<LaneUpdate>();

const [, gap] = inject<[Ref<string>, Ref<string>]>('swimlane-sizes');

const {
  getOpportunityCountForLane,
  getOpportunityHasContract,
  markOpportunityWon,
  updateLane,
} = useOpportunities();

const onLaneChange = async (newLane: number, oldLane: number, leadId: number) => {
  laneUpdate.value = {
    oldLane: oldLane,
    newLane: newLane,
    leadId: leadId,
  };

  if (props.visibleSwimlanes.find(lane => lane.id === newLane)?.state === 'completed') {
    const hasContract = await getOpportunityHasContract(leadId);
    if (hasContract) {
      showOpportunityLostDialog.value = false;
      markOpportunityWon(laneUpdate.value);
      return;
    }
    showOpportunityLostDialog.value = true;
  } else {
    showOpportunityLostDialog.value = false;
    updateLane(laneUpdate.value);
  }
};

const onConfirmOpportunityLost = (reasons: number[]) => {
  showOpportunityLostDialog.value = false;
  updateLane(laneUpdate.value, reasons);
  laneUpdate.value = undefined;
};

const onCancelOpportunityLost = () => {
  laneUpdate.value = undefined;
  showOpportunityLostDialog.value = false;
};

</script>
<template>
  <div class="w-full h-full overflow-x-auto">
    <div v-if="props.allSwimlanes.length > 0 && props.visibleSwimlanes.length === 0" class="flex items-center justify-center">
      <h2 class="text-lg font-normal text-[var(--swimlane-primary-text)]">
        All swimlanes are currently hidden by your visible lane settings
      </h2>
    </div>
    <div v-if="props.visibleSwimlanes.length > 0" class="h-full flex flex-nowrap" :style="{gap}">
      <Lane
        v-for="lane in props.visibleSwimlanes"
        :id="lane.id"
        :key="lane.id"
        :type="type"
        :order="lane.order"
        :status="lane.status"
        :state="lane.state"
        :opportunity-count="getOpportunityCountForLane(type, lane.id)"
        @update:change-lane="onLaneChange"
      >
        <VirtualScroller :items="props.opportunities.filter(opportunity => opportunity.swimlaneId === lane.id)" :item-size="86">
          <template #item="{ item }">
            <LeadCard
              :key="item.id"
              :valid-send-to-lanes="props.allSwimlanes.filter(lane => lane.id !== item.swimlaneId)"
              v-bind="(item as Opportunity)"
              @update:change-lane="onLaneChange"
            />
          </template>
        </VirtualScroller>
      </Lane>
    </div>
  </div>
  <OpportunityLostDialog
    :type="props.type"
    :visible="showOpportunityLostDialog"
    @close="onCancelOpportunityLost"
    @on:confirm="onConfirmOpportunityLost"
  />
</template>
